import * as React from "react";
import {useCallback, useEffect, useMemo, useReducer, useRef} from "react";
import {Virtuoso} from "react-virtuoso";
import makeStyles from "@mui/styles/makeStyles";
import {useSearchParams} from "react-router-dom";
import {useAtom, useAtomValue} from "jotai";
import Checkbox from "@mui/material/Checkbox";
import {RangeListData, RangeListProps, RangeFilterValue, RangeTab, rangeSelectionAtom, RangeCommand, rangeColumnsSettingsAtom, rangeListColumnsAtom, RangeListColumn, rangePendingReloadsAtom} from "./RangeListTypes";
import {loadCustomColumns} from "./RangeColumnPersistence";
import {mergeForcedColumns} from "./RangeColumnMerge";
import {RangeCommands} from "./RangeCommands";
import {SkeletonRow} from "./SkeletonRow";
import {RangeHeader, RangeHeaderData} from "./RangeHeader";
import {buildGraphQLFilter} from "./RangeUtility";
import {parseURLToHeaderData, getRangeListURLParams, updateSearchParams, serializeFilters, headerDataToFilterValues, filtersToFilterObjects} from "./RangeUrlSerializer";
import {RangeTabs, CUSTOM_TAB_ID} from "./RangeTab";

/** Debounce delay in ms before loading data after scroll stops */
const SCROLL_DEBOUNCE_MS = 150;

/** Default row height used before measurement */
const DEFAULT_ROW_HEIGHT = 45;

/** Maximum virtual items to prevent browser height limits (Firefox has issues above ~7M px) */
const MAX_VIRTUAL_ITEMS = 150000;

/** Action types for the data reducer */
type RangeListDataAction<T> =
   | { type: 'START_LOADING_PAGE'; page: number }
   | { type: 'PAGE_LOADED'; page: number; startIndex: number; items: T[]; totalCount: number }
   | { type: 'PAGE_LOAD_ERROR'; page: number }
   | { type: 'RESET_DATA' }
   | { type: 'UPDATE_ROWS'; updates: Partial<T>[]; idField: string };

/** Reducer for managing RangeList data state */
function rangeListDataReducer<T>(state: RangeListData<T>, action: RangeListDataAction<T>): RangeListData<T> {
   switch (action.type) {
      case 'START_LOADING_PAGE': {
         const newLoadingPages = new Set(state.loadingPages);
         newLoadingPages.add(action.page);
         return {
            ...state,
            loadingPages: newLoadingPages,
         };
      }
      case 'PAGE_LOADED': {
         const newItems = new Map(state.items);
         action.items.forEach((item, i) => {
            const index = action.startIndex + i;
            newItems.set(index, item);
         });
         const newLoadingPages = new Set(state.loadingPages);
         newLoadingPages.delete(action.page);
         return {
            items: newItems,
            loadingPages: newLoadingPages,
            totalCount: action.totalCount,
         };
      }
      case 'PAGE_LOAD_ERROR': {
         const newLoadingPages = new Set(state.loadingPages);
         newLoadingPages.delete(action.page);
         return {
            ...state,
            loadingPages: newLoadingPages,
         };
      }
      case 'RESET_DATA': {
         return {
            items: new Map(),
            loadingPages: new Set(),
            totalCount: null,
         };
      }
      case 'UPDATE_ROWS': {
         const newItems = new Map(state.items);
         const {updates, idField} = action;

         // Build ID-to-index map for O(1) lookups (O(n) once instead of O(n) per update)
         const idToIndex = new Map<string, number>();
         for (const [index, item] of newItems.entries()) {
            const itemId = (item as any)[idField];
            if (itemId) {
               idToIndex.set(itemId, index);
            }
         }

         // Apply updates using the map (O(m) instead of O(n*m))
         for (const update of updates) {
            const updateId = (update as any)[idField];
            if (!updateId) continue;

            const index = idToIndex.get(updateId);
            if (index === undefined) continue;

            const item = newItems.get(index);
            if (!item) continue;

            // Merge only fields that exist in the update
            const mergedItem = {...item};
            for (const key of Object.keys(update)) {
               if (key in item) {
                  (mergedItem as any)[key] = (update as any)[key];
               }
            }
            newItems.set(index, mergedItem);
         }

         return {
            ...state,
            items: newItems,
         };
      }
      default:
         return state;
   }
}

/** Initial data state factory */
function createInitialDataState<T>(): RangeListData<T> {
   return {
      items: new Map(),
      loadingPages: new Set(),
      totalCount: null,
   };
}

/** UI state for RangeList */
interface RangeListUIState {
   headerData: RangeHeaderData;
   activeTab: string;
   selectAllLoading: boolean;
   tabsWithCounts: RangeTab[];
   tabCountsLoaded: boolean;
}

/** Action types for the UI reducer */
type RangeListUIAction =
   | { type: 'SET_HEADER_DATA'; payload: RangeHeaderData }
   | { type: 'SET_ACTIVE_TAB'; payload: string }
   | { type: 'SET_SELECT_ALL_LOADING'; payload: boolean }
   | { type: 'SET_TAB_AND_HEADER'; activeTab: string; headerData: RangeHeaderData }
   | { type: 'SET_TABS'; payload: RangeTab[] }
   | { type: 'UPDATE_TAB_COUNTS'; payload: { tabId: string; count: number }[] }
   | { type: 'SET_TAB_COUNTS_LOADED'; payload: boolean };

/** Reducer for managing RangeList UI state */
function rangeListUIReducer(state: RangeListUIState, action: RangeListUIAction): RangeListUIState {
   switch (action.type) {
      case 'SET_HEADER_DATA':
         return {...state, headerData: action.payload};
      case 'SET_ACTIVE_TAB':
         return {...state, activeTab: action.payload};
      case 'SET_SELECT_ALL_LOADING':
         return {...state, selectAllLoading: action.payload};
      case 'SET_TAB_AND_HEADER':
         return {...state, activeTab: action.activeTab, headerData: action.headerData};
      case 'SET_TABS':
         return {...state, tabsWithCounts: action.payload, tabCountsLoaded: false};
      case 'UPDATE_TAB_COUNTS':
         return {
            ...state,
            tabsWithCounts: state.tabsWithCounts.map(tab => {
               const update = action.payload.find(u => u.tabId === tab.id);
               return update ? {...tab, count: update.count} : tab;
            })
         };
      case 'SET_TAB_COUNTS_LOADED':
         return {...state, tabCountsLoaded: action.payload};
      default:
         return state;
   }
}


/**
 * Check if current filters are a superset of tab filters.
 * Returns true if all tab filter values are present in current filters
 * (i.e., nothing has been removed from tab's required filters).
 */
function isFiltersSupersetOfTab(currentFilters: RangeFilterValue[], tabFilters: RangeFilterValue[]): boolean {
   // Every tab filter must be satisfied by current filters
   for (const tabFilter of tabFilters) {
      const matchingCurrent = currentFilters.find(f => f.filterId === tabFilter.filterId);

      // If the tab requires this filter but it's not in current, it's been removed
      if (!matchingCurrent) {
         return false;
      }

      // If requireAll flag differs, semantics changed - treat as removal
      // Note: treat undefined as false for comparison (undefined == false semantically)
      const tabRequireAll = tabFilter.requireAll ?? false;
      const currentRequireAll = matchingCurrent.requireAll ?? false;
      if (currentRequireAll !== tabRequireAll) {
         return false;
      }

      // All tab filter values must be present in current filter values
      for (const tabValue of tabFilter.values) {
         if (!matchingCurrent.values.includes(tabValue)) {
            return false;
         }
      }
   }

   return true;
}

/**
 * Get the difference between current filters and tab filters.
 * Returns only the additions (new filters or additional values) for URL serialization.
 */
function getFilterDifferenceFromTab(currentFilters: RangeFilterValue[], tabFilters: RangeFilterValue[]): RangeFilterValue[] {
   const delta: RangeFilterValue[] = [];

   for (const currentFilter of currentFilters) {
      const tabFilter = tabFilters.find(f => f.filterId === currentFilter.filterId);

      if (!tabFilter) {
         // Entirely new filter not in tab - include whole filter
         delta.push(currentFilter);
      } else {
         // Filter exists in tab - only include additional values
         const additionalValues = currentFilter.values.filter(v => !tabFilter.values.includes(v));
         if (additionalValues.length > 0) {
            delta.push({
               filterId: currentFilter.filterId,
               values: additionalValues,
               requireAll: currentFilter.requireAll
            });
         }
      }
   }

   return delta;
}

/**
 * Merge tab's base filters with URL delta filters.
 * Used when loading from URL to reconstruct full filter state.
 */
function mergeTabFiltersWithDelta(tabFilters: RangeFilterValue[], deltaFilters: RangeFilterValue[]): RangeFilterValue[] {
   const merged: RangeFilterValue[] = [];

   // Start with tab filters
   for (const tabFilter of tabFilters) {
      const deltaFilter = deltaFilters.find(f => f.filterId === tabFilter.filterId);
      if (deltaFilter) {
         // Merge tab values with delta values
         const combinedValues = [...new Set([...tabFilter.values, ...deltaFilter.values])];
         merged.push({
            filterId: tabFilter.filterId,
            values: combinedValues,
            requireAll: tabFilter.requireAll
         });
      } else {
         // No delta for this filter, use tab filter as-is
         merged.push({ ...tabFilter });
      }
   }

   // Add any delta filters that aren't in tab filters
   for (const deltaFilter of deltaFilters) {
      if (!tabFilters.find(f => f.filterId === deltaFilter.filterId)) {
         merged.push({ ...deltaFilter });
      }
   }

   return merged;
}

/**
 * Check if two filter sets match exactly (same filters with same values).
 * Normalizes requireAll (treats undefined as false).
 */
function doFiltersMatch(filtersA: RangeFilterValue[], filtersB: RangeFilterValue[]): boolean {
   if (filtersA.length !== filtersB.length) {
      return false;
   }

   for (const filterA of filtersA) {
      const requireAllA = filterA.requireAll ?? false;
      const matchingFilter = filtersB.find(f =>
         f.filterId === filterA.filterId &&
         (f.requireAll ?? false) === requireAllA &&
         f.values.length === filterA.values.length &&
         f.values.every(v => filterA.values.includes(v))
      );
      if (!matchingFilter) {
         return false;
      }
   }

   return true;
}

/** Find matching tab for given filters */
function findMatchingTabForFilters(tabs: RangeTab[] | undefined, urlFilters: RangeFilterValue[]): string {
   if (!tabs?.length) return CUSTOM_TAB_ID;

   if (urlFilters.length === 0) {
      return tabs[0].id;
   }

   for (const tab of tabs) {
      if (doFiltersMatch(tab.filters || [], urlFilters)) {
         return tab.id;
      }
   }

   return CUSTOM_TAB_ID;
}

/** Options for creating initial UI state */
interface CreateInitialUIStateOptions {
   searchParams: URLSearchParams;
   tabs: RangeTab[] | undefined;
   disableUrlState?: boolean;
   initialSearch?: string;
   initialFilters?: RangeFilterValue[];
}

/** Create initial UI state from URL and tabs */
function createInitialUIState(opts: CreateInitialUIStateOptions): RangeListUIState {
   const { searchParams, tabs, disableUrlState, initialSearch, initialFilters } = opts;

   // When URL state is disabled, use initial values instead of URL params
   let headerData: RangeHeaderData;
   let urlFilters: RangeFilterValue[];

   if (disableUrlState) {
      // Use initial values
      const { filterAnd, filterOr } = initialFilters ? filtersToFilterObjects(initialFilters) : { filterAnd: {}, filterOr: {} };
      headerData = {
         search: initialSearch || '',
         sort: '',
         filterAnd,
         filterOr,
      };
      urlFilters = initialFilters || [];
   } else {
      // Parse from URL
      headerData = parseURLToHeaderData(searchParams);
      urlFilters = headerDataToFilterValues(headerData);
   }

   // Check for explicit tab parameter in URL first (only when URL state is enabled)
   const urlTab = !disableUrlState ? searchParams.get('tab') : null;
   const tabFromUrl = urlTab && tabs?.find(t => t.id === urlTab) ? urlTab : null;
   const selectedTab = tabFromUrl ? tabs?.find(t => t.id === tabFromUrl) : null;

   // If tab is explicitly in URL, merge tab's base filters with URL delta
   // Otherwise, infer tab from filters (exact match only for initial load)
   let activeTab: string;
   let effectiveHeaderData = headerData;

   if (tabFromUrl && selectedTab) {
      // Tab explicitly specified - URL filters are delta, merge with tab's base filters
      activeTab = tabFromUrl;
      const tabFilters = selectedTab.filters || [];
      const mergedFilters = mergeTabFiltersWithDelta(tabFilters, urlFilters);
      const { filterAnd, filterOr } = filtersToFilterObjects(mergedFilters);

      effectiveHeaderData = {
         ...headerData,
         filterAnd,
         filterOr,
      };
   } else {
      // No explicit tab - infer from filters or default to first tab
      const hasFilters = urlFilters.length > 0;

      if (!hasFilters && tabs?.length) {
         // No filters, default to first tab
         activeTab = tabs[0].id;
         const firstTab = tabs[0];
         if (firstTab.filters?.length > 0) {
            const { filterAnd, filterOr } = filtersToFilterObjects(firstTab.filters);
            effectiveHeaderData = {
               ...headerData,
               filterAnd,
               filterOr,
            };
         }
      } else {
         // Has filters but no tab - find exact match or go custom
         activeTab = findMatchingTabForFilters(tabs, urlFilters);
      }
   }

   return {
      headerData: effectiveHeaderData,
      activeTab,
      selectAllLoading: false,
      tabsWithCounts: tabs || [],
      tabCountsLoaded: false,
   };
}

/**
 * RangeList - A virtual list component that loads data on-demand based on scroll position
 *
 * Uses react-virtuoso's totalCount + rangeChanged pattern to:
 * - Show full scrollbar immediately (user can drag to any position)
 * - Load only the visible data range
 * - Cache loaded data for instant re-display when scrolling back
 */
export const RangeList = <T extends object>({
                                               globalId,
                                               columns,
                                               itemsPerPage,
                                               onRequestData,
                                               options,
                                               onSelectRow,
                                               context,
                                               filters,
                                               sorts,
                                               searchLabel,
                                               tabs,
                                               selections,
                                               onSelectAll,
                                               commands,
                                               initialSearch,
                                               initialFilters,
                                               onTabChange,
                                               onSearchChange,
                                               onReloadRows,
                                            }: RangeListProps<T>) => {
   const classes = useStyles();
   const [searchParams, setSearchParams] = useSearchParams();

   // Sparse data storage - only loaded items are in the map
   const [data, dispatchData] = useReducer(
      rangeListDataReducer as React.Reducer<RangeListData<T>, RangeListDataAction<T>>,
      null,
      createInitialDataState
   );

   // UI state - headerData, activeTab, selectAllLoading
   const [uiState, dispatchUI] = useReducer(
      rangeListUIReducer,
      {searchParams, tabs, disableUrlState: options?.disableUrlState, initialSearch, initialFilters},
      (opts) => createInitialUIState(opts)
   );

   // Destructure for convenience
   const {headerData, activeTab, selectAllLoading, tabsWithCounts, tabCountsLoaded} = uiState;

   // Selection state from Jotai atom
   const [selection, setSelection] = useAtom(rangeSelectionAtom(globalId));

   // Column settings state from Jotai atoms
   const [columnsSettings, setColumnsSettings] = useAtom(rangeColumnsSettingsAtom(globalId));
   const visibleColumns = useAtomValue(rangeListColumnsAtom(globalId));

   // Pending row reloads from external components (e.g., detail pages)
   const [pendingReloads, setPendingReloads] = useAtom(rangePendingReloadsAtom(globalId));

   // Track loading to prevent duplicate requests
   const loadingPagesRef = useRef<Set<number>>(new Set());

   // Track latest request ID to cancel stale requests
   const latestRequestIdRef = useRef<number>(0);

   // Debounce timer for scroll-triggered loading
   const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
   const pendingRangeRef = useRef<{ startIndex: number; endIndex: number } | null>(null);

   // Helper to compute visible columns for a tab, applying both columns (include) and columnsExcluded (exclude)
   // Falls back to first tab's columns when tab is undefined (e.g., custom tab)
   const getTabColumns = useCallback((tab: RangeTab | undefined): string[] | undefined => {
      const effectiveTab = tab || tabs?.[0];
      if (!effectiveTab) return undefined;

      // Start with tab.columns if specified, otherwise undefined (show all)
      let result = effectiveTab.columns;

      // Apply exclusions if specified
      if (effectiveTab.columnsExcluded && effectiveTab.columnsExcluded.length > 0) {
         // If no columns specified, start with all column IDs
         const baseColumns = result || columns.map(c => c.id);
         result = baseColumns.filter(colId => !effectiveTab.columnsExcluded!.includes(colId));
      }

      return result;
   }, [columns, tabs]);

   // Helper to update column settings for a tab (persisted columns take priority, except for the tab's forced columns)
   const updateColumnsForTab = useCallback((tab: RangeTab | undefined) => {
      const persistedColumns = loadCustomColumns(globalId);
      const tabColumns = getTabColumns(tab);
      const effectiveTab = tab || tabs?.[0];
      const visibleColumns = persistedColumns
         ? mergeForcedColumns(persistedColumns, effectiveTab?.columnsForced, tabColumns)
         : null;
      setColumnsSettings(prev => ({
         ...prev,
         columnsVisible: visibleColumns || tabColumns,
         defaultColumnsVisible: tabColumns
      }));
   }, [globalId, setColumnsSettings, getTabColumns, tabs]);

   // Initialize column settings on mount
   useEffect(() => {
      const activeTabObj = tabs?.find(t => t.id === activeTab);
      setColumnsSettings(prev => ({
         ...prev,
         columns: columns
      }));
      updateColumnsForTab(activeTabObj);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // Update tabs when props change
   useEffect(() => {
      dispatchUI({type: 'SET_TABS', payload: tabs || []});
   }, [tabs]);

   // Load tab counts after initialization
   useEffect(() => {
      if (tabCountsLoaded || data.totalCount === null || tabsWithCounts.length === 0) return;

      const loadTabCounts = async () => {
         try {
            const tabUpdates: { tabId: string; count: number }[] = [];

            for (const tab of tabsWithCounts) {
               if (tab.showCount) {
                  // Use countFilters if specified, otherwise fall back to filters
                  const filtersForCount = tab.countFilters || tab.filters;
                  const tabFilter = buildGraphQLFilter(filters, filtersForCount);
                  const filterToPass = Object.keys(tabFilter).length > 0 ? tabFilter : undefined;
                  const response = await onRequestData(undefined, undefined, 1, 1, filterToPass);
                  tabUpdates.push({tabId: tab.id, count: response.totalCount});
               }
            }

            dispatchUI({type: 'UPDATE_TAB_COUNTS', payload: tabUpdates});
            dispatchUI({type: 'SET_TAB_COUNTS_LOADED', payload: true});
         } catch (error) {
            console.error('Error loading tab counts:', error);
         }
      };

      loadTabCounts();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.totalCount, tabCountsLoaded, tabsWithCounts.length]);

   // Check if current filters match a specific tab's predefined filters
   const filtersMatchTab = useCallback((tab: RangeTab, currentFilters: RangeFilterValue[]): boolean => {
      return doFiltersMatch(tab.filters || [], currentFilters);
   }, []);

   // Find which tab (if any) matches the current filter state
   const findMatchingTab = useCallback((currentFilters: RangeFilterValue[]): RangeTab | undefined => {
      if (!tabs?.length) return undefined;
      return tabs.find(tab => filtersMatchTab(tab, currentFilters));
   }, [tabs, filtersMatchTab]);

   // Handle tab change from RangeTabs component
   const handleTabChange = useCallback((tabId: string) => {
      const tab = tabs?.find(t => t.id === tabId);
      if (!tab) return;

      // Update column visibility
      updateColumnsForTab(tab);

      // Update headerData with tab's predefined filters
      const { filterAnd, filterOr } = filtersToFilterObjects(tab.filters);
      const newHeaderData: RangeHeaderData = {
         ...headerData,
         filterAnd,
         filterOr,
      };

      // Update both tab and header in a single dispatch
      dispatchUI({type: 'SET_TAB_AND_HEADER', activeTab: tabId, headerData: newHeaderData});

      // Update URL - don't serialize filters for known tabs, just clear them
      // The tab selection implies the filters, so we only need search, sort, and tab
      if (!options?.disableUrlState) {
         const newParams = updateSearchParams(
            searchParams,
            {
               search: newHeaderData.search || null,
               filters: null, // Clear filters from URL since tab implies them
               sort: newHeaderData.sort || null,
               tab: tabId
            }
         );
         setSearchParams(newParams, {replace: true});
      }

      // Call onTabChange callback
      onTabChange?.(tabId);
   }, [tabs, headerData, searchParams, setSearchParams, updateColumnsForTab, options?.disableUrlState, onTabChange]);

   // Build current GraphQL filter from header state
   const currentFilter = useMemo(() => {
      const filterValues = headerDataToFilterValues(headerData);
      return buildGraphQLFilter(filters, filterValues);
   }, [filters, headerData]);

   // Cleanup debounce timer on unmount
   useEffect(() => {
      return () => {
         if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
         }
      };
   }, []);

   // Store refs for comparison (to detect changes that require data reload)
   const filterRef = useRef(currentFilter);
   const searchRef = useRef(headerData.search);
   const sortRef = useRef(headerData.sort);

   // Reset data when filters, search, or sort change
   useEffect(() => {
      const filterChanged = JSON.stringify(filterRef.current) !== JSON.stringify(currentFilter);
      const searchChanged = searchRef.current !== headerData.search;
      const sortChanged = sortRef.current !== headerData.sort;

      if (filterChanged || searchChanged || sortChanged) {
         filterRef.current = currentFilter;
         searchRef.current = headerData.search;
         sortRef.current = headerData.sort;
         // Increment request ID to cancel any in-flight requests
         latestRequestIdRef.current = Date.now();
         // Reset data - the load effect below will trigger reload
         dispatchData({type: 'RESET_DATA'});
         loadingPagesRef.current.clear();
         // Clear selection when filters/search/sort change
         setSelection({selected: []});
      }
   }, [currentFilter, headerData.search, headerData.sort, setSelection]);

   // Handle select all click
   const handleSelectAllClick = useCallback(async () => {
      // If there are any selections, clear them. Otherwise select all.
      if (selection.selected.length > 0) {
         setSelection({selected: []});
      } else if (onSelectAll) {
         dispatchUI({type: 'SET_SELECT_ALL_LOADING', payload: true});
         try {
            const filterToPass = Object.keys(currentFilter).length > 0 ? currentFilter : undefined;
            const allIds = await onSelectAll(filterToPass);
            setSelection({selected: allIds});
         } catch (error) {
            console.error('Error fetching all IDs for selection:', error);
         } finally {
            dispatchUI({type: 'SET_SELECT_ALL_LOADING', payload: false});
         }
      }
   }, [onSelectAll, currentFilter, setSelection, selection.selected.length]);

   // Handle individual row checkbox click
   const handleRowCheckboxClick = useCallback((event: React.MouseEvent, id: string) => {
      event.preventDefault();
      event.stopPropagation();

      setSelection(prev => {
         const selectedIndex = prev.selected.indexOf(id);
         let newSelected: string[];

         if (selectedIndex === -1) {
            // Add to selection
            newSelected = [...prev.selected, id];
         } else {
            // Remove from selection
            newSelected = prev.selected.filter((_, i) => i !== selectedIndex);
         }

         return {selected: newSelected};
      });
   }, [setSelection]);

   // Handle header changes (filter/search/sort changes)
   const handleHeaderChanged = useCallback((newHeaderData: RangeHeaderData) => {
      const newFilterValues = headerDataToFilterValues(newHeaderData);

      // First check if we can stay on the current tab (superset logic)
      // We stay on the tab if the new filters still include all of the tab's required filters
      const currentTabObj = tabs?.find(t => t.id === activeTab);
      const currentTabFilters = currentTabObj?.filters || [];

      let newActiveTab: string;
      let filterDelta: RangeFilterValue[] | null = null;

      if (currentTabObj && activeTab !== CUSTOM_TAB_ID) {
         // Check if new filters are a superset of current tab's filters
         if (isFiltersSupersetOfTab(newFilterValues, currentTabFilters)) {
            // Stay on current tab - filters are a superset (only additions)
            newActiveTab = activeTab;
            filterDelta = getFilterDifferenceFromTab(newFilterValues, currentTabFilters);
         } else {
            // Something was removed from tab's required filters - check for exact match with other tabs
            const matchingTab = findMatchingTab(newFilterValues);
            if (matchingTab) {
               newActiveTab = matchingTab.id;
               filterDelta = getFilterDifferenceFromTab(newFilterValues, matchingTab.filters || []);
            } else {
               // No match - go to Custom tab
               newActiveTab = tabs?.length ? CUSTOM_TAB_ID : activeTab;
            }
         }
      } else {
         // Currently on Custom tab or no tabs - check for exact match first
         const matchingTab = findMatchingTab(newFilterValues);
         if (matchingTab) {
            newActiveTab = matchingTab.id;
            filterDelta = getFilterDifferenceFromTab(newFilterValues, matchingTab.filters || []);
         } else if (tabs?.length) {
            // No exact match - check if filters are superset of any tab (to transition back from Custom)
            const supersetTab = tabs.find(tab => isFiltersSupersetOfTab(newFilterValues, tab.filters || []));
            if (supersetTab) {
               newActiveTab = supersetTab.id;
               filterDelta = getFilterDifferenceFromTab(newFilterValues, supersetTab.filters || []);
            } else {
               newActiveTab = CUSTOM_TAB_ID;
            }
         } else {
            newActiveTab = activeTab;
         }
      }

      const isOnKnownTab = newActiveTab !== CUSTOM_TAB_ID && tabs?.find(t => t.id === newActiveTab);

      // When transitioning to a known tab, update columns to that tab's columns
      // When transitioning to custom tab, keep current columns (don't change anything)
      if (isOnKnownTab) {
         const tabForColumns = tabs?.find(t => t.id === newActiveTab);
         updateColumnsForTab(tabForColumns);
      }
      // Note: When going to Custom tab, we intentionally keep current columns

      // Update header and tab in a single dispatch
      dispatchUI({type: 'SET_TAB_AND_HEADER', activeTab: newActiveTab, headerData: newHeaderData});

      // Update URL - for known tabs, only serialize the delta (additional filters beyond tab's base)
      if (!options?.disableUrlState) {
         if (isOnKnownTab && filterDelta !== null) {
            // Serialize only the delta filters
            const deltaFilterStr = filterDelta.length > 0 ? serializeFilters(filterDelta) : null;
            const newParams = updateSearchParams(searchParams, {
               search: newHeaderData.search || null,
               filters: deltaFilterStr,
               sort: newHeaderData.sort || null,
               tab: newActiveTab
            });
            setSearchParams(newParams, {replace: true});
         } else {
            // Custom tab - serialize all filters
            const urlParams = getRangeListURLParams(newHeaderData, newActiveTab);
            const newParams = updateSearchParams(searchParams, urlParams);
            setSearchParams(newParams, {replace: true});
         }
      }

      // Call onTabChange callback if tab changed
      if (newActiveTab !== activeTab) {
         onTabChange?.(newActiveTab);
      }

      // Call onSearchChange callback if search changed
      if (newHeaderData.search !== headerData.search) {
         onSearchChange?.(newHeaderData.search || '');
      }
   }, [searchParams, setSearchParams, findMatchingTab, tabs, activeTab, updateColumnsForTab, options?.disableUrlState, onTabChange, onSearchChange, headerData.search]);

   // Selection column width (fixed 40px)
   const selectionColumnWidth = '40px';

   // Effective columns to render (visible columns if set, otherwise all columns)
   const effectiveColumns = useMemo(() => {
      return visibleColumns.length > 0 ? visibleColumns : columns;
   }, [visibleColumns, columns]);

   // Calculate row height from visible columns' minHeight requirements
   const rowHeight = useMemo(() => {
      const baseHeight = options?.itemHeight ?? DEFAULT_ROW_HEIGHT;
      const columnHeights = effectiveColumns.map(col => col.minHeight ?? DEFAULT_ROW_HEIGHT);
      return Math.max(baseHeight, ...columnHeights);
   }, [options?.itemHeight, effectiveColumns]);

   // Calculate column widths for grid template
   const gridTemplateColumns = useMemo(() => {
      const totalWidth = effectiveColumns.reduce((a, b) => a + (b.width || 1), 0) || 1;
      // When selections enabled, data columns share the remaining space after the fixed selection column
      const availableWidth = selections ? `calc(100% - ${selectionColumnWidth})` : '100%';
      const dataColumns = effectiveColumns.map(col => {
         const pct = ((col.width || 1) / totalWidth) * 100;
         return selections ? `calc(${availableWidth} * ${pct / 100})` : `${pct}%`;
      }).join(' ');
      return selections ? `${selectionColumnWidth} ${dataColumns}` : dataColumns;
   }, [effectiveColumns, selections]);

   // Selection state helpers
   const hasSelections = selection.selected.length > 0;
   const isAllSelected = data.totalCount !== null && data.totalCount > 0 && selection.selected.length >= data.totalCount;
   const isIndeterminate = hasSelections && !isAllSelected;
   const isSelected = useCallback((id: string) => selection.selected.includes(id), [selection.selected]);

   // Calculate which page an index belongs to
   const getPageForIndex = useCallback((index: number): number => {
      return Math.floor(index / itemsPerPage) + 1; // 1-indexed pages
   }, [itemsPerPage]);

   // Load a specific page of data
   const loadPage = useCallback(async (page: number) => {
      // Skip if already loading
      if (loadingPagesRef.current.has(page)) {
         return;
      }

      // Skip if page is beyond the virtual item limit
      const maxPage = Math.ceil(MAX_VIRTUAL_ITEMS / itemsPerPage);
      if (page > maxPage) {
         return;
      }

      // Check if page is already fully loaded (only if we know totalCount)
      const startIndex = (page - 1) * itemsPerPage;
      if (data.totalCount !== null) {
         const cappedTotal = Math.min(data.totalCount, MAX_VIRTUAL_ITEMS);
         const endIndex = Math.min(startIndex + itemsPerPage, cappedTotal);
         let allLoaded = true;
         for (let i = startIndex; i < endIndex; i++) {
            if (!data.items.has(i)) {
               allLoaded = false;
               break;
            }
         }
         if (allLoaded) {
            return;
         }
      }

      // Mark as loading
      loadingPagesRef.current.add(page);
      dispatchData({type: 'START_LOADING_PAGE', page});

      // Capture request ID before async call to detect stale responses
      const currentRequestId = latestRequestIdRef.current;

      try {
         // Pass search, sort, and filter to onRequestData
         const searchToPass = headerData.search || undefined;
         // Convert sort ID to sort value
         const sortValue = sorts?.find(s => s.id === headerData.sort)?.value;
         const filterToPass = Object.keys(currentFilter).length > 0 ? currentFilter : undefined;
         const response = await onRequestData(searchToPass, sortValue, page, itemsPerPage, filterToPass);

         // Only update state if this is still the latest request
         if (latestRequestIdRef.current === currentRequestId) {
            dispatchData({type: 'PAGE_LOADED', page, startIndex, items: response.data, totalCount: response.totalCount});
         }
      } catch (error) {
         // Only log error if this is still the latest request
         if (latestRequestIdRef.current === currentRequestId) {
            console.error(`Error loading page ${page}:`, error);
            dispatchData({type: 'PAGE_LOAD_ERROR', page});
         }
      } finally {
         loadingPagesRef.current.delete(page);
      }
   }, [data.items, data.totalCount, itemsPerPage, onRequestData, currentFilter, headerData.search, headerData.sort, sorts]);

   // Initial fetch and reload after filter reset - load first page to get totalCount
   useEffect(() => {
      // Only load if we don't have data yet (initial load or after reset)
      if (data.totalCount === null && !loadingPagesRef.current.has(1)) {
         loadPage(1);
      }
   }, [data.totalCount, loadPage]);

   // Load pages for a given range
   const loadPagesForRange = useCallback((range: { startIndex: number; endIndex: number }) => {
      const startPage = getPageForIndex(range.startIndex);
      const endPage = getPageForIndex(range.endIndex);

      // Load all pages in the visible range
      for (let page = startPage; page <= endPage; page++) {
         loadPage(page);
      }
   }, [getPageForIndex, loadPage]);

   // Handle visible range changes - debounce to avoid loading data while scrolling fast
   const handleRangeChanged = useCallback((range: { startIndex: number; endIndex: number }) => {
      // Cap the range to MAX_VIRTUAL_ITEMS
      const cappedRange = {
         startIndex: Math.min(range.startIndex, MAX_VIRTUAL_ITEMS - 1),
         endIndex: Math.min(range.endIndex, MAX_VIRTUAL_ITEMS - 1)
      };

      // Store the latest range
      pendingRangeRef.current = cappedRange;

      // Clear existing timer
      if (debounceTimerRef.current) {
         clearTimeout(debounceTimerRef.current);
      }

      // Set new timer to load after scrolling stops
      debounceTimerRef.current = setTimeout(() => {
         if (pendingRangeRef.current) {
            loadPagesForRange(pendingRangeRef.current);
            pendingRangeRef.current = null;
         }
      }, SCROLL_DEBOUNCE_MS);
   }, [loadPagesForRange]);

   // Get cell value from a column definition
   const getCellValue = (column: RangeListColumn<T>, row: T): any => {
      if (column.get !== undefined) {
         return column.get(row);
      } else if (column.field !== undefined) {
         return (row as any)[column.field];
      }
      return null;
   };

   const idField = options?.idField || '_id';

   // Generic row update function - can be called from columns via context
   const updateRows = useCallback((updates: Partial<T>[]) => {
      if (updates && updates.length > 0) {
         dispatchData({type: 'UPDATE_ROWS', updates, idField});
      }
   }, [idField]);

   // Handle command execution with optional row updates
   const handleCommandExecuted = useCallback((updates?: Partial<T>[]) => {
      if (updates) {
         updateRows(updates);
      }
      // Clear selection after command execution
      setSelection({selected: []});
   }, [updateRows, setSelection]);

   // Function to reload rows by ID (for columns to use)
   const reloadRows = useCallback((ids: string[]) => {
      if (onReloadRows) {
         onReloadRows(ids).then(reloadedRows => {
            if (reloadedRows.length > 0) {
               updateRows(reloadedRows as Partial<T>[]);
            }
         }).catch(error => {
            console.error('Error reloading rows:', error);
         });
      }
   }, [onReloadRows, updateRows]);

   // Merge updateRows and reloadRows into context for column access
   const effectiveContext = useMemo(() => ({
      ...context,
      updateRows,
      reloadRows
   }), [context, updateRows, reloadRows]);

   // Process pending row reloads from external components (e.g., detail pages)
   useEffect(() => {
      if (pendingReloads.ids.length > 0 && onReloadRows) {
         const idsToReload = [...pendingReloads.ids];
         setPendingReloads({ids: []});

         onReloadRows(idsToReload).then(reloadedRows => {
            if (reloadedRows.length > 0) {
               updateRows(reloadedRows as Partial<T>[]);
            }
         }).catch(error => {
            console.error('Error reloading rows:', error);
         });
      }
   }, [pendingReloads, onReloadRows, updateRows, setPendingReloads]);

   // Calculate skeleton row count based on viewport height
   const skeletonRowCount = useMemo(() => {
      if (options?.height) {
         // Use configured height
         const height = typeof options.height === 'number' ? options.height : parseInt(options.height, 10) || 600;
         return Math.ceil(height / rowHeight);
      }
      // Fall back to window height estimate (subtract some for header/chrome)
      return Math.ceil((typeof window !== 'undefined' ? window.innerHeight - 200 : 600) / rowHeight);
   }, [options?.height, rowHeight]);

   // Determine if we should show the header (only if filters or sorts are provided)
   const showHeader = filters && filters.length > 0;

   // Determine if we should show the tabs
   const showTabs = tabsWithCounts && tabsWithCounts.length > 0;

   // Use actual totalCount or estimate for initial render (keeps Virtuoso mounted to prevent flicker)
   const isInitialLoading = data.totalCount === null;
   const displayTotalCount = data.totalCount ?? skeletonRowCount;

   return (
      <div className={classes.container}>
         {/* Tabs */}
         {showTabs && (
            <RangeTabs
               tabs={tabsWithCounts}
               tabId={activeTab}
               onTabChange={handleTabChange}
            />
         )}
         {/* Search/Filter header - hidden when rows are selected */}
         {showHeader && !hasSelections && (
            <RangeHeader
               globalId={globalId}
               count={isInitialLoading ? undefined : data.totalCount!}
               filters={filters}
               sorts={sorts}
               searchLabel={searchLabel}
               headerData={headerData}
               onHeaderChanged={handleHeaderChanged}
               isSearchLoading={isInitialLoading}
            />
         )}
         {/* Commands header - shown when rows are selected */}
         {hasSelections && (
            <RangeCommands
               selectedIds={selection.selected}
               commands={commands}
               onClearSelection={() => setSelection({selected: []})}
               onCommandExecuted={handleCommandExecuted}
            />
         )}
         {/* Fixed column header */}
         <div
            className={classes.headerRow}
            style={{gridTemplateColumns}}
         >
            {/* Selection column header */}
            {selections && (
               <div className={classes.headerCell}>
                  {onSelectAll && (
                     <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllSelected}
                        disabled={selectAllLoading}
                        onChange={handleSelectAllClick}
                        inputProps={{'aria-label': 'select all'}}
                        size="small"
                        sx={{
                           paddingTop: 0,
                           paddingBottom: 0
                        }}
                     />
                  )}
               </div>
            )}
            {effectiveColumns.map((column, index) => (
               <div
                  className={classes.headerCell}
                  key={`header_${column.id}_${index}`}
               >
                  {column.label}
               </div>
            ))}
         </div>

         {/* Virtual list - key forces remount when rowHeight changes */}
         <Virtuoso
            key={rowHeight}
            totalCount={Math.min(displayTotalCount, MAX_VIRTUAL_ITEMS)}
            rangeChanged={handleRangeChanged}
            fixedItemHeight={rowHeight}
            useWindowScroll={!options?.height}
            style={options?.height ? {width: '100%', height: options.height} : {width: '100%'}}
            increaseViewportBy={options?.height ? Number(options.height) : 800}
            overscan={60}
            itemContent={(index) => {
               const item = data.items.get(index);

               // Show skeleton if item not loaded
               if (!item) {
                  return (
                     <SkeletonRow
                        columnCount={effectiveColumns.length + (selections ? 1 : 0)}
                        gridTemplateColumns={gridTemplateColumns}
                        itemHeight={rowHeight}
                     />
                  );
               }

               // Render actual row
               const id = (item as any)[idField];
               const itemSelected = isSelected(id);

               return (
                  <div
                     className={classes.dataRow}
                     style={{
                        gridTemplateColumns,
                        height: rowHeight,
                        minHeight: rowHeight,
                        maxHeight: rowHeight,
                        cursor: onSelectRow ? 'pointer' : undefined,
                     }}
                     onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onSelectRow?.(item, e.ctrlKey);
                     }}
                  >
                     {/* Selection checkbox cell */}
                     {selections && (
                        <div className={classes.cell}>
                           <Checkbox
                              checked={itemSelected}
                              onClick={(e) => handleRowCheckboxClick(e, id)}
                              inputProps={{'aria-labelledby': `row-${id}`}}
                              size="small"
                           />
                        </div>
                     )}
                     {effectiveColumns.map((column, colIndex) => {
                        const value = getCellValue(column, item);
                        let content: React.ReactNode;

                        if (column.render !== undefined) {
                           content = column.render(value, item, effectiveContext);
                        } else {
                           content = value;
                        }

                        let title: string | undefined;
                        if (typeof value === 'string') {
                           title = value;
                        }

                        return (
                           <div
                              key={`cell_${id}_${column.id}_${colIndex}`}
                              className={column.ellipsis ? classes.cellEllipsis : classes.cell}
                              title={title}
                           >
                              {content}
                           </div>
                        );
                     })}
                  </div>
               );
            }}
         />
      </div>
   );
};

const useStyles = makeStyles({
   container: {
      display: 'flex',
      flexDirection: 'column',
   },
   headerRow: {
      display: 'grid',
      alignItems: 'center',
      backgroundColor: '#F4F4F4',
   },
   headerCell: {
      fontWeight: 600,
      padding: '8px 4px',
   },
   dataRow: {
      display: 'grid',
      alignItems: 'center',
      // Use box-shadow instead of border to avoid browser subpixel rendering glitch
      // See: https://github.com/petyosi/react-virtuoso/issues/1042
      boxShadow: 'inset 0 1px 0 0 #e0e0e0',
      boxSizing: 'border-box',
      overflow: 'hidden',
   },
   cell: {
      padding: '8px 4px',
      overflow: 'hidden',
   },
   cellEllipsis: {
      padding: '8px 4px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
   },
});
